// src/services/bookletService.js (Fixed ID consistency)
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';
import { getDocument } from 'pdfjs-dist';

const BOOKLETS_COLLECTION = 'docs-booklets';

export const bookletService = {
  // Create booklet with proper ID handling
  async createBookletFromPDF(pdfFile, bookletData, userId) {
    try {
      // Step 1: Create document WITHOUT internal ID field
      const bookletDoc = {
        title: bookletData.title || 'Untitled Booklet',
        description: bookletData.description || '',
        userId,
        createdBy: userId,
        createdByName: bookletData.createdByName || '',
        type: 'image-booklet',
        status: 'draft',
        visibility: 'private',
        downloadAllowed: false,
        printAllowed: false,
        pageCount: 0, // Will be updated
        dimensions: { width: 0, height: 0 }, // Will be updated
        thumbnailUrl: '', // Will be updated
        pages: [], // Will be populated
        settings: {
          pageTransition: bookletData.pageTransition || 'flip',
          theme: bookletData.theme || 'modern',
          showNavigation: true,
          showPageNumbers: true,
          allowDownload: false,
          backgroundColor: '#ffffff',
          ...bookletData.settings
        },
        publishSettings: {
          isPublic: false,
          requirePassword: false,
          seoTitle: bookletData.title || '',
          seoDescription: bookletData.description || '',
          allowAnalytics: true
        },
        analytics: {
          views: 0,
          uniqueViews: 0,
          lastViewedAt: null
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Step 2: Add to Firestore to get the REAL document ID
      const docRef = await addDoc(collection(db, BOOKLETS_COLLECTION), bookletDoc);
      const firestoreDocumentId = docRef.id; // This is the REAL ID we must use everywhere
      
      console.log('✅ Created booklet with Firestore ID:', firestoreDocumentId);
      
      // Step 3: Convert PDF using the REAL Firestore document ID
      const pages = await this.convertPDFToImages(pdfFile, firestoreDocumentId, userId);
      
      // Step 4: Update document with pages (still NO internal id field)
      const updateData = {
        pages: pages,
        pageCount: pages.length,
        dimensions: pages[0]?.dimensions || { width: 0, height: 0 },
        thumbnailUrl: pages[0]?.thumbnailUrl || '',
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);
      
      console.log('✅ Updated booklet with', pages.length, 'pages');

      // Step 5: Return the complete booklet with Firestore ID
      return { 
        id: firestoreDocumentId, // Use REAL Firestore ID
        ...bookletDoc,
        ...updateData,
        // Convert serverTimestamp back for client
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('❌ Error creating booklet:', error);
      throw error;
    }
  },

  // Convert PDF using the REAL document ID
  async convertPDFToImages(pdfFile, realDocumentId, userId, quality = 2) {
    try {
      console.log('🔄 Converting PDF for booklet ID:', realDocumentId);
      
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdf = await getDocument({
        data: pdfBytes,
        useWorkerFetch: false,
        isEvalSupported: false
      }).promise;

      const pages = [];
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: quality });
        
        // Render main image
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport }).promise;
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/jpeg', 0.9);
        });
        
        // Upload using REAL document ID in path
        const fileName = `page-${pageNum}.jpg`;
        const pageRef = ref(storage, `booklets/${userId}/${realDocumentId}/pages/${fileName}`);
        const snapshot = await uploadBytes(pageRef, blob);
        const imageUrl = await getDownloadURL(snapshot.ref);
        
        // Create thumbnail
        const thumbScale = 0.2;
        const thumbViewport = page.getViewport({ scale: thumbScale });
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = thumbViewport.width;
        thumbCanvas.height = thumbViewport.height;
        const thumbContext = thumbCanvas.getContext('2d');
        await page.render({ canvasContext: thumbContext, viewport: thumbViewport }).promise;
        
        const thumbBlob = await new Promise(resolve => {
          thumbCanvas.toBlob(resolve, 'image/jpeg', 0.7);
        });
        
        // Upload thumbnail using REAL document ID
        const thumbFileName = `thumb-${pageNum}.jpg`;
        const thumbRef = ref(storage, `booklets/${userId}/${realDocumentId}/thumbnails/${thumbFileName}`);
        const thumbSnapshot = await uploadBytes(thumbRef, thumbBlob);
        const thumbnailUrl = await getDownloadURL(thumbSnapshot.ref);
        
        pages.push({
          pageNumber: pageNum,
          url: imageUrl,
          thumbnailUrl: thumbnailUrl,
          storagePath: snapshot.ref.fullPath,
          thumbnailPath: thumbSnapshot.ref.fullPath,
          dimensions: {
            width: viewport.width,
            height: viewport.height
          },
          originalName: `page-${pageNum}.jpg`,
          rotation: 0,
          interactions: []
        });
        
        // Clean up canvases
        canvas.remove();
        thumbCanvas.remove();
      }
      
      await pdf.destroy();
      console.log('✅ PDF conversion complete. Total pages:', pages.length);
      return pages;
    } catch (error) {
      console.error('❌ Error converting PDF:', error);
      throw error;
    }
  },

  // Get booklet by Firestore document ID
  async getBooklet(documentId, requireAuth = true) {
    try {
      if (!documentId) {
        throw new Error('Document ID is required');
      }
      
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.log('❌ Booklet not found:', documentId);
        throw new Error('Booklet not found');
      }

      const bookletData = docSnap.data();
      
      // Check access permissions
      const isPublished = bookletData.status === 'published' && 
                         (bookletData.visibility === 'public' || bookletData.publishSettings?.isPublic);
      const isOwner = auth.currentUser && bookletData.userId === auth.currentUser.uid;

      if (requireAuth && !isOwner && !isPublished) {
        throw new Error('Access denied: Booklet is private');
      }

      console.log('✅ Booklet loaded successfully:', bookletData.title);
      
      // Return booklet with Firestore document ID
      return { 
        id: docSnap.id, // Use the REAL Firestore document ID
        ...bookletData,
        pages: bookletData.pages || []
      };
    } catch (error) {
      console.error('❌ Error fetching booklet:', error);
      throw error;
    }
  },

  // Get public booklet (no auth required)
  async getPublicBooklet(documentId) {
    return this.getBooklet(documentId, false);
  },

  // Get user's booklets
  async getUserBooklets(userId) {
    try {
      console.log('🔍 Fetching booklets for user:', userId);
      
      const q = query(
        collection(db, BOOKLETS_COLLECTION),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const booklets = querySnapshot.docs.map(doc => ({
        id: doc.id, // Use Firestore document ID
        ...doc.data()
      }));
      
      console.log('✅ Found', booklets.length, 'booklets');
      return booklets;
    } catch (error) {
      console.error('❌ Error fetching user booklets:', error);
      throw error;
    }
  },

  // Update booklet
  async updateBooklet(documentId, updates) {
    try {
      console.log('📝 Updating booklet:', documentId);
      
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Booklet updated successfully');
    } catch (error) {
      console.error('❌ Error updating booklet:', error);
      throw error;
    }
  },

  // Add interaction to page
  async addPageInteraction(documentId, pageIndex, interaction) {
    try {
      console.log('➕ Adding interaction to page', pageIndex, 'of booklet', documentId);
      
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Booklet not found');
      }

      const bookletData = docSnap.data();
      const pages = [...(bookletData.pages || [])];
      
      if (!pages[pageIndex]) {
        throw new Error(`Page ${pageIndex} not found`);
      }

      // Add interaction with unique ID
      const newInteraction = {
        ...interaction,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      };

      // Update the page's interactions
      pages[pageIndex] = {
        ...pages[pageIndex],
        interactions: [...(pages[pageIndex].interactions || []), newInteraction]
      };

      // Update document
      await updateDoc(docRef, {
        pages: pages,
        updatedAt: serverTimestamp()
      });

      console.log('✅ Interaction added successfully');
      return newInteraction;
    } catch (error) {
      console.error('❌ Error adding interaction:', error);
      throw error;
    }
  },

  // Delete page interaction
  async deletePageInteraction(documentId, pageIndex, interactionId) {
    try {
      console.log('🗑️ Deleting interaction', interactionId, 'from page', pageIndex);
      
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Booklet not found');
      }

      const bookletData = docSnap.data();
      const pages = [...(bookletData.pages || [])];
      
      if (!pages[pageIndex]) {
        throw new Error(`Page ${pageIndex} not found`);
      }

      // Remove the interaction
      pages[pageIndex] = {
        ...pages[pageIndex],
        interactions: pages[pageIndex].interactions.filter(i => i.id !== interactionId)
      };

      // Update document
      await updateDoc(docRef, {
        pages: pages,
        updatedAt: serverTimestamp()
      });

      console.log('✅ Interaction deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting interaction:', error);
      throw error;
    }
  },

  // Publish booklet
  async publishBooklet(documentId, publishSettings) {
    try {
      console.log('🌐 Publishing booklet:', documentId);
      
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      await updateDoc(docRef, {
        status: 'published',
        visibility: publishSettings.isPublic ? 'public' : 'private',
        publishSettings: publishSettings,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      const publicUrl = `${window.location.origin}/booklet/${documentId}`;
      console.log('✅ Booklet published at:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('❌ Error publishing booklet:', error);
      throw error;
    }
  },

  // Track booklet view
  async trackBookletView(documentId, viewData = {}) {
    try {
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentAnalytics = docSnap.data().analytics || {};
        await updateDoc(docRef, {
          'analytics.views': (currentAnalytics.views || 0) + 1,
          'analytics.lastViewedAt': serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('❌ Error tracking view:', error);
      // Don't throw for analytics errors
    }
  },

  // Delete booklet
  async deleteBooklet(documentId, userId) {
    try {
      console.log('🗑️ Deleting booklet:', documentId);
      
      // Get booklet data first
      const booklet = await this.getBooklet(documentId);
      
      // Delete all images from storage
      if (booklet.pages) {
        for (const page of booklet.pages) {
          // Delete main image
          if (page.storagePath) {
            const imageRef = ref(storage, page.storagePath);
            await deleteObject(imageRef).catch(console.error);
          }
          // Delete thumbnail
          if (page.thumbnailPath) {
            const thumbRef = ref(storage, page.thumbnailPath);
            await deleteObject(thumbRef).catch(console.error);
          }
        }
      }
      
      // Delete Firestore document
      const docRef = doc(db, BOOKLETS_COLLECTION, documentId);
      await deleteDoc(docRef);
      
      console.log('✅ Booklet deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting booklet:', error);
      throw error;
    }
  }
};